import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getApiUrl, fetchWithAuth } from '@/api'

interface TimeSlotItem {
  start_time: string
  end_time: string
  label: string
}

interface SpaceFormState {
  name: string
  description: string
  image_url: string
  location: string
  min_capacity: string
  max_capacity: string
  daily_limit_per_member: string
  reservation_notice: string
  is_reservable: number
  is_visible: number
  time_slots: TimeSlotItem[]
}

const EMPTY_FORM: SpaceFormState = {
  name: '',
  description: '',
  image_url: '',
  location: '',
  min_capacity: '1',
  max_capacity: '10',
  daily_limit_per_member: '1',
  reservation_notice: '',
  is_reservable: 1,
  is_visible: 1,
  time_slots: [],
}

export default function AdminSpaceForm() {
  const { space_id } = useParams<{ space_id: string }>()
  const navigate = useNavigate()
  const isEdit = !!space_id

  const [form, setForm] = useState<SpaceFormState>(EMPTY_FORM)
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (isEdit) fetchSpace()
  }, [space_id])

  const fetchSpace = async () => {
    setLoading(true)
    try {
      const [spaceRes, slotsRes] = await Promise.all([
        fetchWithAuth(getApiUrl(`/marubit/api/space/${space_id}`)),
        fetchWithAuth(getApiUrl(`/marubit/api/space/admin/${space_id}/time-slots`)),
      ])
      if (!spaceRes.ok) throw new Error('조회 실패')
      const space = await spaceRes.json()
      const slots = slotsRes.ok ? await slotsRes.json() : { time_slots: [] }

      setForm({
        name: space.name ?? '',
        description: space.description ?? '',
        image_url: space.image_url ?? '',
        location: space.location ?? '',
        min_capacity: String(space.min_capacity ?? 1),
        max_capacity: String(space.max_capacity ?? 10),
        daily_limit_per_member: String(space.daily_limit_per_member ?? 1),
        reservation_notice: space.reservation_notice ?? '',
        is_reservable: space.is_reservable ?? 1,
        is_visible: space.is_visible ?? 1,
        time_slots: (slots.time_slots ?? []).map((s: { start_time: string; end_time: string; label: string | null }) => ({
          start_time: s.start_time.slice(0, 5),
          end_time: s.end_time.slice(0, 5),
          label: s.label ?? '',
        })),
      })
    } catch {
      alert('공간 정보를 불러오는 데 실패했습니다.')
      navigate('/admin/space?tab=spaces')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field: keyof SpaceFormState, value: string | number) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  // ─── TimeSlot handlers ───────────────────────────────────────────

  const addTimeSlot = () => {
    setForm(prev => ({
      ...prev,
      time_slots: [...prev.time_slots, { start_time: '09:00', end_time: '10:00', label: '' }],
    }))
  }

  const removeTimeSlot = (idx: number) => {
    setForm(prev => ({
      ...prev,
      time_slots: prev.time_slots.filter((_, i) => i !== idx),
    }))
  }

  const updateTimeSlot = (idx: number, field: keyof TimeSlotItem, value: string) => {
    setForm(prev => ({
      ...prev,
      time_slots: prev.time_slots.map((slot, i) =>
        i === idx ? { ...slot, [field]: value } : slot
      ),
    }))
  }

  // ─── Submit ──────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!form.name.trim()) { alert('공간명을 입력해주세요.'); return }
    if (Number(form.max_capacity) < Number(form.min_capacity)) {
      alert('최대 인원은 최소 인원보다 크거나 같아야 합니다.'); return
    }
    if (!window.confirm(isEdit ? '수정 내용을 저장하시겠습니까?' : '공간을 등록하시겠습니까?')) return

    setSaving(true)
    try {
      const body = {
        name: form.name,
        description: form.description || null,
        image_url: form.image_url || null,
        location: form.location || null,
        min_capacity: Number(form.min_capacity),
        max_capacity: Number(form.max_capacity),
        daily_limit_per_member: form.daily_limit_per_member ? Number(form.daily_limit_per_member) : null,
        reservation_notice: form.reservation_notice || null,
        is_reservable: form.is_reservable,
        is_visible: form.is_visible,
        time_slots: form.time_slots.length > 0
          ? form.time_slots.map(s => ({
              start_time: s.start_time,
              end_time: s.end_time,
              label: s.label || null,
            }))
          : null,
      }

      const res = await fetchWithAuth(
        getApiUrl(isEdit ? `/marubit/api/space/admin/${space_id}` : '/marubit/api/space/admin'),
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      )
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '저장 실패') }

      alert(isEdit ? '수정되었습니다.' : '공간이 등록되었습니다.')
      navigate('/admin/space?tab=spaces')
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '저장에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-[#f5f2f0] min-h-screen p-8 flex items-center justify-center">
        <p className="text-gray-300 text-[13px]">불러오는 중...</p>
      </div>
    )
  }

  return (
    <div className="bg-[#f5f2f0] min-h-screen p-8">
      <div className="max-w-[1200px] mx-auto">

        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-[22px] font-extrabold text-[#2a1f1a] mb-1">
              {isEdit ? '공간 수정' : '공간 등록'}
            </h1>
            <p className="text-[13px] text-gray-400">
              {new Date().toLocaleDateString('ko-KR', {
                year: 'numeric', month: 'long', day: 'numeric', weekday: 'long',
              })}
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/space?tab=spaces')}
            className="text-[13px] text-[#887267] font-semibold px-4 py-2 rounded-lg border border-[#e8d8ce] bg-white cursor-pointer hover:bg-[#f5f2f0] transition-colors"
          >
            ← 목록으로
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-[#e8e0dc] px-6 py-6 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">

          {/* 기본 정보 */}
          <SectionTitle>기본 정보</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 mb-6">
            <Field label="공간명 *" full>
              <input
                value={form.name}
                onChange={e => handleChange('name', e.target.value)}
                placeholder="공간 이름"
                className={inputCls}
              />
            </Field>
            <Field label="위치">
              <input
                value={form.location}
                onChange={e => handleChange('location', e.target.value)}
                placeholder="예: 3층 회의실"
                className={inputCls}
              />
            </Field>
            <Field label="이미지 URL">
              <input
                value={form.image_url}
                onChange={e => handleChange('image_url', e.target.value)}
                placeholder="https://..."
                className={inputCls}
              />
            </Field>
            <Field label="예약 가능 여부">
              <select
                value={form.is_reservable}
                onChange={e => handleChange('is_reservable', Number(e.target.value))}
                className={inputCls}
              >
                <option value={1}>예약 가능</option>
                <option value={0}>예약 불가</option>
              </select>
            </Field>
            <Field label="노출 여부">
              <select
                value={form.is_visible}
                onChange={e => handleChange('is_visible', Number(e.target.value))}
                className={inputCls}
              >
                <option value={1}>노출</option>
                <option value={0}>숨김</option>
              </select>
            </Field>
          </div>

          <div className="border-t border-[#f5f2f0] my-4" />

          {/* 이용 인원 */}
          <SectionTitle>이용 인원</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 mb-6">
            <Field label="최소 인원 (명)">
              <input
                type="number" min={1}
                value={form.min_capacity}
                onChange={e => handleChange('min_capacity', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field label="최대 인원 (명)">
              <input
                type="number" min={1}
                value={form.max_capacity}
                onChange={e => handleChange('max_capacity', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field label="1인 하루 최대 예약 (회)">
              <input
                type="number" min={1}
                value={form.daily_limit_per_member}
                onChange={e => handleChange('daily_limit_per_member', e.target.value)}
                placeholder="비우면 무제한"
                className={inputCls}
              />
            </Field>
          </div>

          <div className="border-t border-[#f5f2f0] my-4" />

          {/* 예약 시간 슬롯 */}
          <div className="flex items-center justify-between">
            <SectionTitle>예약 시간 슬롯</SectionTitle>
            <button
              onClick={addTimeSlot}
              className="text-[12px] text-[#887267] font-semibold px-3 py-1.5 rounded-lg border border-[#e8d8ce] bg-white cursor-pointer hover:bg-[#f5f2f0] transition-colors"
            >
              + 슬롯 추가
            </button>
          </div>

          <div className="mt-3 mb-6 flex flex-col gap-2">
            {form.time_slots.length === 0 ? (
              <p className="text-[12px] text-gray-300 py-3">등록된 시간 슬롯이 없습니다. 슬롯을 추가해주세요.</p>
            ) : form.time_slots.map((slot, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-[#fdf8f0] rounded-xl px-4 py-3 border border-[#e8e0dc]">
                <div className="flex items-center gap-1.5">
                  <input
                    type="time"
                    value={slot.start_time}
                    onChange={e => updateTimeSlot(idx, 'start_time', e.target.value)}
                    className="border border-[#e8e0dc] rounded-lg px-2 py-1.5 text-[12px] text-[#2a1f1a] outline-none focus:border-[#887267] bg-white"
                  />
                  <span className="text-[12px] text-gray-400">~</span>
                  <input
                    type="time"
                    value={slot.end_time}
                    onChange={e => updateTimeSlot(idx, 'end_time', e.target.value)}
                    className="border border-[#e8e0dc] rounded-lg px-2 py-1.5 text-[12px] text-[#2a1f1a] outline-none focus:border-[#887267] bg-white"
                  />
                </div>
                <input
                  value={slot.label}
                  onChange={e => updateTimeSlot(idx, 'label', e.target.value)}
                  placeholder="라벨 (예: 오전)"
                  className="flex-1 border border-[#e8e0dc] rounded-lg px-3 py-1.5 text-[12px] text-[#2a1f1a] outline-none focus:border-[#887267] bg-white"
                />
                <button
                  onClick={() => removeTimeSlot(idx)}
                  className="text-[12px] text-red-400 hover:text-red-600 border border-red-100 rounded-lg px-2 py-1.5 bg-white cursor-pointer transition-colors"
                >
                  삭제
                </button>
              </div>
            ))}
          </div>

          <div className="border-t border-[#f5f2f0] my-4" />

          {/* 안내사항 */}
          <SectionTitle>예약 안내사항</SectionTitle>
          <div className="mt-3 mb-6">
            <textarea
              value={form.reservation_notice}
              onChange={e => handleChange('reservation_notice', e.target.value)}
              placeholder="예약 시 안내할 내용을 입력해주세요."
              rows={4}
              className="w-full border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] transition-colors resize-none"
            />
          </div>

          <div className="border-t border-[#f5f2f0] my-4" />

          {/* 공간 설명 */}
          <SectionTitle>공간 설명</SectionTitle>
          <div className="mt-3">
            <textarea
              value={form.description}
              onChange={e => handleChange('description', e.target.value)}
              placeholder="공간에 대한 설명을 입력해주세요."
              rows={4}
              className="w-full border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] transition-colors resize-none"
            />
          </div>

          {/* Actions */}
          <div className="border-t border-[#f5f2f0] mt-6 pt-5 flex justify-end gap-2">
            <button
              onClick={() => navigate('/admin/space?tab=spaces')}
              className="text-[13px] text-[#887267] font-semibold px-5 py-2.5 rounded-lg border border-[#e8d8ce] bg-white cursor-pointer hover:bg-[#f5f2f0] transition-colors"
            >
              취소
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="bg-[#887267] text-white text-[13px] font-semibold px-6 py-2.5 rounded-lg border-none cursor-pointer hover:bg-[#6b5248] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? '저장 중...' : isEdit ? '수정하기' : '등록하기'}
            </button>
          </div>

        </div>
      </div>
    </div>
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const inputCls =
  'w-full border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] transition-colors bg-white'

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="text-[13px] font-bold text-[#5a3d32]">{children}</h2>
}

function Field({ label, children, full }: { label: string; children: React.ReactNode; full?: boolean }) {
  return (
    <div className={`flex flex-col gap-1 ${full ? 'sm:col-span-2' : ''}`}>
      <label className="text-[11px] text-[#5a3d32] font-medium">{label}</label>
      {children}
    </div>
  )
}